(() => {
  'use strict';

  const sections = document.querySelectorAll('[data-f2w-sport-detail-final]');
  if (!sections.length) return;

  const TYPES = {
    fighters: ['fighter_profile'],
    legends: ['fighter_profile'],
    trainers: ['trainer_profile'],
    masters: ['trainer_profile'],
    founders: ['trainer_profile'],
    promoters: ['trainer_profile'],
    gyms: ['sports_location'],
    venues: ['sports_location'],
    iconic: ['sports_location'],
    events: ['event'],
    historic: ['event'],
    organizations: ['organization']
  };

  const normalize = (value) => String(value || '')
    .toLocaleLowerCase('nl-NL')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const flagHtml = (code, label) => {
    const flagCode = String(code || '')
      .trim()
      .toLowerCase();

    const isNation =
      flagCode === 'gb-sct' ||
      flagCode === 'gb-eng' ||
      flagCode === 'gb-wls';

    if (
      !flagCode ||
      (flagCode.length !== 2 && !isNation)
    ) {
      return '';
    }

    const src =
      `https://flagcdn.com/w40/${flagCode}.png`;
    const srcset =
      `https://flagcdn.com/w80/${flagCode}.png 2x`;

    return `<span class="f2w-country-flag"><img src="${escapeHtml(src)}" srcset="${escapeHtml(srcset)}" alt="${escapeHtml(label)}" width="20" height="15" loading="lazy" decoding="async">&nbsp;</span>`;
  };

  const plain = (value) => String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const richTextPlain = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';

    try {
      const parsed = JSON.parse(raw);

      const walk = (node) => {
        if (!node) return '';
        if (Array.isArray(node)) {
          return node.map(walk).filter(Boolean).join(' ');
        }
        if (typeof node === 'string') return node;
        if (typeof node !== 'object') return '';
        if (node.type === 'text' && typeof node.value === 'string') {
          return node.value;
        }
        if (Array.isArray(node.children)) {
          return walk(node.children);
        }
        if (typeof node.value === 'string') return node.value;
        return '';
      };

      const extracted = plain(walk(parsed));
      if (extracted) return extracted;
    } catch (error) {
      // Keep plain text fields working when the value is not JSON.
    }

    return plain(raw);
  };

  const textList = (value) => parseValue(value)
    .map((item) => plain(item))
    .filter(Boolean);

  const parseValue = (value) => {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        return parsed.flatMap((item) => {
          if (item && typeof item === 'object') {
            return item.value || item.id || item.gid || '';
          }

          return parseValue(item);
        }).filter(Boolean);
      }

      if (parsed && typeof parsed === 'object') {
        return [
          parsed.value || parsed.id || parsed.gid || ''
        ].filter(Boolean);
      }

      if (typeof parsed === 'string') {
        return parsed ? [parsed] : [];
      }
    } catch (error) {
      // Shopify can return a plain GID or a pipe-separated value.
    }

    return String(value)
      .split(/[|,]/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const gids = (value) => parseValue(value)
    .filter((item) => String(item).startsWith('gid://'));

  const fieldMap = (fields) => (fields || []).reduce((map, field) => {
    map[field.key] = field.value;
    return map;
  }, {});

  const field = (fields, ...keys) => {
    for (const key of keys) {
      if (
        fields[key] !== undefined &&
        fields[key] !== null &&
        String(fields[key]).trim()
      ) {
        return fields[key];
      }
    }

    return '';
  };

  const localizedUrl = (value) => {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);

      if (!['http:', 'https:'].includes(url.protocol)) {
        return '';
      }

      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return '';
    }
  };

  const today = () => new Date().toISOString().slice(0, 10);

  const isTrue = (value) => [
    'true',
    '1',
    'yes',
    'ja'
  ].includes(normalize(value));

  const imageFromReference = (reference) => {
    if (!reference) return '';
    if (reference.__typename === 'MediaImage') {
      return reference.image?.url || '';
    }
    return '';
  };

  const mergeImageMaps = (root, target, loadedPages, totalPages) => {
    root.querySelectorAll('[data-f2w-sport-image-map]').forEach((source) => {
      const type = String(source.dataset.imageType || '');
      const page = Math.max(
        1,
        parseInt(source.dataset.imagePage, 10) || 1
      );
      const pages = Math.max(
        page,
        parseInt(source.dataset.imagePages, 10) || page
      );

      if (type) {
        if (!loadedPages.has(type)) {
          loadedPages.set(type, new Set());
        }

        loadedPages.get(type).add(page);
        totalPages.set(
          type,
          Math.max(totalPages.get(type) || 1, pages)
        );
      }

      try {
        const entries = JSON.parse(source.textContent || '[]');

        entries.forEach((entry) => {
          if (entry?.key && entry?.image) {
            target.set(String(entry.key), String(entry.image));
          }
        });
      } catch (error) {
        // A malformed fallback map must not replace valid Storefront images.
      }
    });
  };

  const queryFor = (type) => `
    query F2WSportDetailDirectory($after: String, $language: LanguageCode)
      @inContext(language: $language) {
      metaobjects(type: "${type}", first: 250, after: $after) {
        nodes {
          id
          handle
          onlineStoreUrl

          name: field(key: "name") { value }
          title: field(key: "title") { value }
          nickname: field(key: "nickname") { value }
          nationalityText: field(key: "nationality") { value }
          specialization: field(key: "specialization") { value }
          locationName: field(key: "location_name") { value }
          significance: field(key: "significance") { value }
          externalUrl: field(key: "external_url") { value }
          shortIntro: field(key: "short_intro") { value }
          intro: field(key: "intro") { value }
          description: field(key: "description") { value }
          bio: field(key: "bio") { value }
          role: field(key: "role") { value }
          roles: field(key: "roles") { value }
          function: field(key: "function") { value }
          functions: field(key: "functions") { value }
          tradition: field(key: "tradition") { value }
          traditions: field(key: "traditions") { value }
          style: field(key: "style") { value }
          styles: field(key: "styles") { value }
          systems: field(key: "systems") { value }
          locationType: field(key: "location_type") { value }
          organizationType: field(key: "organization_type") { value }
          type: field(key: "type") { value }
          editorialProfile: field(key: "editorial_profile") { value }
          profile: field(key: "profile") { value }
          status: field(key: "status") { value }
          isLegend: field(key: "is_legend") { value }
          city: field(key: "city") { value }
          place: field(key: "plaats") { value }
          region: field(key: "region") { value }
          province: field(key: "province") { value }
          state: field(key: "state") { value }
          era: field(key: "era") { value }
          period: field(key: "period") { value }
          decade: field(key: "decade") { value }
          dateStart: field(key: "date_start") { value }
          startDate: field(key: "start_date") { value }
          date: field(key: "date") { value }
          dateEnd: field(key: "date_end") { value }
          endDate: field(key: "end_date") { value }
          isHistoric: field(key: "is_historic") { value }
          historic: field(key: "historic") { value }
          historical: field(key: "historical") { value }

          sports: field(key: "sports") {
            references(first: 250) {
              nodes {
                ... on Metaobject {
                  id
                  handle
                  name: field(key: "name") { value }
                }
              }
            }
          }

          sport: field(key: "sport") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
              }
            }
          }

          country: field(key: "country") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
                code: field(key: "code") { value }
              }
            }
          }

          nationality: field(key: "nationality") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
                code: field(key: "code") { value }
              }
            }
          }

          location: field(key: "location") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
              }
            }
          }

          venue: field(key: "venue") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
              }
            }
          }

          homeGym: field(key: "home_gym") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
              }
            }
          }

          gym: field(key: "gym") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
              }
            }
          }

          organization: field(key: "organization") {
            reference {
              ... on Metaobject {
                id
                handle
                name: field(key: "name") { value }
              }
            }
          }

          organizations: field(key: "organizations") {
            references(first: 250) {
              nodes {
                ... on Metaobject {
                  id
                  handle
                  name: field(key: "name") { value }
                }
              }
            }
          }

          organisaties: field(key: "organisaties") {
            references(first: 250) {
              nodes {
                ... on Metaobject {
                  id
                  handle
                  name: field(key: "name") { value }
                }
              }
            }
          }

          coverImage: field(key: "cover_image") {
            reference {
              ... on MediaImage {
                image { url(transform: { maxWidth: 1200 }) }
              }
            }
          }

          photo: field(key: "photo") {
            reference {
              ... on MediaImage {
                image { url(transform: { maxWidth: 1200 }) }
              }
            }
          }

          logo: field(key: "logo") {
            reference {
              ... on MediaImage {
                image { url(transform: { maxWidth: 1200 }) }
              }
            }
          }

          image: field(key: "image") {
            reference {
              ... on MediaImage {
                image { url(transform: { maxWidth: 1200 }) }
              }
            }
          }

          images: field(key: "images") {
            references(first: 10) {
              nodes {
                ... on MediaImage {
                  image { url(transform: { maxWidth: 1200 }) }
                }
              }
            }
          }
        }

        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const referenceQuery = `
    query F2WSportDetailReferences($ids: [ID!]!) {
      nodes(ids: $ids) {
        __typename

        ... on Metaobject {
          id
          handle
          fields {
            key
            value
          }
        }

        ... on MediaImage {
          id
          image {
            url
          }
        }
      }
    }
  `;

  const storefrontLanguage = (root) => {
    return String(
      root.dataset.language ||
      document.documentElement?.lang ||
      ''
    )
      .split('-')[0]
      .trim()
      .toUpperCase() || null;
  };

  const sourceLanguage = (root) => {
    return String(
      root.dataset.sourceLanguage ||
      'NL'
    )
      .split('-')[0]
      .trim()
      .toUpperCase() || 'NL';
  };

  const request = async (root, query, variables) => {
    const token = root.dataset.storefrontToken || '';
    const shop = root.dataset.shopDomain ||
      window.Shopify?.shop ||
      window.location.hostname;
    const version = root.dataset.apiVersion || '2024-10';
    const endpoint = `https://${shop}/api/${version}/graphql.json`;

    if (!token) {
      throw new Error('Missing Storefront token');
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query, variables })
    });

    if (!response.ok) {
      throw new Error('Storefront request failed');
    }

    const payload = await response.json();

    if (payload.errors?.length) {
      throw new Error('Storefront query failed');
    }

    return payload.data || {};
  };

  const fetchAll = async (root, type, language) => {
    const records = [];
    let after = null;
    const seen = new Set();

    do {
      if (after && seen.has(after)) {
        throw new Error('Repeated Storefront cursor');
      }

      if (after) seen.add(after);

      const data = await request(
        root,
        queryFor(type),
        {
          after,
          language: language || null
        }
      );

      const page = data.metaobjects;

      if (!page) {
        throw new Error(`No ${type} data returned`);
      }

      records.push(...(page.nodes || []));
      after = page.pageInfo?.hasNextPage
        ? page.pageInfo.endCursor
        : null;

      if (page.pageInfo?.hasNextPage && !after) {
        throw new Error('Missing next cursor');
      }
    } while (after);

    return records;
  };

  const resolveReferences = async (root, records) => {
    const ids = new Set();

    records.forEach((record) => {
      const fields = fieldMap(record.fields);

      Object.values(fields).forEach((value) => {
        gids(value).forEach((id) => ids.add(id));
      });
    });

    const resolved = new Map();
    const allIds = [...ids];

    for (let index = 0; index < allIds.length; index += 100) {
      const page = allIds.slice(index, index + 100);

      if (!page.length) continue;

      const data = await request(
        root,
        referenceQuery,
        { ids: page }
      );

      (data.nodes || []).forEach((node) => {
        resolved.set(node.id, node);
      });
    }

    return resolved;
  };

  const scalar = (record, ...keys) => {
    for (const key of keys) {
      const value = record[key]?.value;

      if (
        value !== undefined &&
        value !== null &&
        String(value).trim()
      ) {
        return value;
      }
    }

    return '';
  };

  const typedReferences = (...fields) => {
    const values = [];

    fields.forEach((item) => {
      if (item?.reference) values.push(item.reference);
      if (item?.references?.nodes) {
        values.push(...item.references.nodes);
      }
    });

    const unique = new Map();

    values.filter(Boolean).forEach((item) => {
      const label = item.name?.value || item.handle || '';

      if (!item.id || !label) return;

      unique.set(item.id, {
        id: item.id,
        label,
        handle: item.handle || '',
        code: item.code?.value || ''
      });
    });

    return [...unique.values()];
  };

  const typedImage = (record) => {
    const fields = [
      record.coverImage,
      record.photo,
      record.logo,
      record.image
    ];

    for (const item of fields) {
      if (item?.reference?.image?.url) {
        return item.reference.image.url;
      }
    }

    const imageNode = record.images?.references?.nodes?.find(
      (item) => item?.image?.url
    );

    return imageNode?.image?.url || '';
  };

  const readRecord = (
    record,
    imageMap,
    type,
    classificationRecord = record
  ) => {
    const fallbackImage = imageMap.get(
      `${type}:${record.handle || ''}`
    ) || '';

    const sports = typedReferences(record.sports, record.sport);
    const locations = typedReferences(
      record.location,
      record.venue,
      record.homeGym,
      record.gym
    );
    const countries = typedReferences(
      record.country,
      record.nationality
    );
    const organizations = typedReferences(
      record.organization,
      record.organizations,
      record.organisaties
    );
    const roles = parseValue(
      scalar(record, 'role', 'roles', 'function', 'functions')
    );
    const traditions = parseValue(
      scalar(record, 'tradition', 'traditions', 'style', 'styles', 'systems')
    );
    const rawType = scalar(
      record,
      'locationType',
      'organizationType',
      'role',
      'type'
    );
    const classificationType = scalar(
      classificationRecord,
      'locationType',
      'organizationType',
      'role',
      'type'
    );
    const classificationProfile = scalar(
      classificationRecord,
      'editorialProfile',
      'profile',
      'status'
    );
    const classificationHistoric = isTrue(
      scalar(
        classificationRecord,
        'isHistoric',
        'historic',
        'historical'
      )
    );
    const classificationLegend = isTrue(
      scalar(classificationRecord, 'isLegend')
    );
    const dateStart = String(
      scalar(record, 'dateStart', 'startDate', 'date') || ''
    ).slice(0, 10);
    const dateEnd = String(
      scalar(
        record,
        'dateEnd',
        'endDate',
        'dateStart',
        'startDate',
        'date'
      ) || ''
    ).slice(0, 10);

    return {
      id: record.id,
      metaobjectType: type,
      handle: record.handle || '',
      url: localizedUrl(record.onlineStoreUrl),
      name: scalar(record, 'name', 'title') || record.handle || '',
      nickname: scalar(record, 'nickname'),
      nationality: scalar(record, 'nationalityText'),
      specialization: scalar(record, 'specialization'),
      locationName: scalar(record, 'locationName'),
      significance: richTextPlain(
        scalar(record, 'significance')
      ),
      externalUrl: localizedUrl(
        scalar(record, 'externalUrl')
      ),
      sports,
      image: typedImage(record) || fallbackImage,
      location: locations[0],
      locations,
      country: countries[0],
      countries,
      organizations,
      roles,
      traditions,
      functions: parseValue(
        scalar(record, 'function', 'functions', 'role')
      ),
      typeLabel: rawType,
      type: normalize(rawType),
      classificationType: normalize(classificationType),
      profile: normalize(
        scalar(record, 'editorialProfile', 'profile', 'status')
      ),
      classificationProfile: normalize(classificationProfile),
      city: scalar(record, 'city', 'place'),
      region: scalar(record, 'region', 'province', 'state'),
      era: textList(
        scalar(record, 'era', 'period')
      ).join(', '),
      decade: scalar(record, 'decade') ||
        (dateStart ? `${dateStart.slice(0, 3)}0` : ''),
      dateStart,
      dateEnd,
      historic: classificationHistoric,
      classificationHistoric,
      legend: classificationLegend,
      classificationLegend,
      description: richTextPlain(
        scalar(record, 'shortIntro', 'intro', 'description', 'bio')
      )
    };
  };

  const matchesKind = (record, kind) => {
    if (kind === 'legends') {
      return record.classificationLegend ||
        record.classificationType.includes('legend') ||
        record.classificationProfile.includes('legend');
    }

    if (kind === 'masters') {
      return record.classificationType.includes('master') ||
        record.classificationType.includes('meester');
    }

    if (kind === 'founders') {
      return record.classificationType.includes('founder') ||
        record.classificationType.includes('grondlegger');
    }

    if (kind === 'promoters') {
      return record.classificationType.includes('promot') ||
        record.classificationProfile.includes('promot');
    }

    if (kind === 'organizations') return true;

    if (kind === 'gyms') {
      return [
        'gym',
        'school',
        'dojo',
        'dojang',
        'sportschool'
      ].some((value) => record.classificationType.includes(value));
    }

    if (kind === 'venues') {
      return record.classificationType === 'venue' ||
        record.classificationType.includes('event_venue');
    }

    if (kind === 'iconic') {
      return record.classificationType.includes('iconic') ||
        record.classificationProfile.includes('iconic');
    }

    if (kind === 'historic') return record.historic;

    if (kind === 'events') {
      const date = record.dateEnd || record.dateStart;
      return !record.historic &&
        Boolean(date && date >= today());
    }

    return true;
  };

  const makeCard = (root, panel, record, kind) => {
    const labels = {
      fighters: root.dataset.labelFighters || '',
      legends: root.dataset.labelLegends || '',
      trainers: root.dataset.labelTrainers || '',
      masters: root.dataset.labelMasters || '',
      founders: root.dataset.labelFounders || '',
      promoters: root.dataset.labelPromoters || '',
      gyms: root.dataset.labelGyms || '',
      venues: root.dataset.labelVenues || '',
      iconic: root.dataset.labelIconic || '',
      events: root.dataset.labelEvents || '',
      historic: root.dataset.labelHistoric || '',
      organizations: root.dataset.labelOrganizations || ''
    };

    const roleLabelMap = {
      trainer:
        root.dataset.labelRoleTrainer || '',
      master:
        root.dataset.labelRoleMaster || '',
      founder:
        root.dataset.labelRoleFounder || '',
      promoter:
        root.dataset.labelRolePromoter || '',
      official:
        root.dataset.labelRoleOfficial || '',
      corner_man:
        root.dataset.labelRoleCornerMan || ''
    };

    const roleKey = (value) => String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');

    const roleLabel = (value) => {
      const key = roleKey(value);

      if (!key) {
        return '';
      }

      if (roleLabelMap[key]) {
        return roleLabelMap[key];
      }

      const words = key.replace(/_/g, ' ');

      return (
        words.charAt(0).toUpperCase() +
        words.slice(1)
      );
    };

    const chapterRoleKey = {
      trainers: 'trainer',
      masters: 'master',
      founders: 'founder',
      promoters: 'promoter'
    };

    const locationKinds = [
      'gyms',
      'venues',
      'iconic'
    ];

    const cardLandBadge =
      panel?.dataset.f2wSportCardLandBadge === 'true';
    const cardLegendBadge =
      panel?.dataset.f2wSportCardLegendBadge === 'true';
    const cardSportTags =
      panel?.dataset.f2wSportCardSportTags === 'true';

    const image = record.image
      ? `<img src="${escapeHtml(record.image)}" alt="${escapeHtml(record.name)}" width="1100" height="825" loading="lazy">`
      : `<div class="f2w-event-card__image--placeholder"><span aria-hidden="true">${escapeHtml(record.name.slice(0, 1))}</span></div>`;

    const badge = (value, modifier = 'upcoming', prefix = '') => value
      ? `<span class="f2w-event-card__period f2w-event-card__period--${modifier}">${prefix}${escapeHtml(value)}</span>`
      : '';

    const line = (className, values, prefix = '') => {
      const output = values.filter(Boolean);

      return output.length
        ? `<p class="${className}">${prefix}${escapeHtml(output.join(', '))}</p>`
        : '';
    };

    const countryFlag = () => flagHtml(
      record.country?.code,
      record.country?.label
    );

    const countryBadgeHtml = () => badge(
      record.country?.label ||
        record.nationality ||
        '',
      'upcoming',
      countryFlag()
    );

    const sports = cardSportTags && record.sports.length
      ? `<div class="f2w-event-card__sports">${record.sports.map((sport) => `<span class="f2w-event-card__sport">${escapeHtml(sport.label)}</span>`).join('')}</div>`
      : '';

    let topline = '';
    let details = '';
    let dateBlock = '';

    if (kind === 'fighters' || kind === 'legends') {
      const primaryBadge = cardLegendBadge
        ? badge(labels.legends)
        : badge(record.nickname);

      const secondaryBadge =
        cardLegendBadge && record.nickname
          ? badge(record.nickname, 'default')
          : '';

      topline = primaryBadge || secondaryBadge
        ? `<div class="f2w-event-card__topline">${primaryBadge}${secondaryBadge}</div>`
        : '';

      details += line(
        'f2w-event-card__location',
        [record.country?.label || record.nationality],
        countryFlag()
      );
    } else if (
      [
        'trainers',
        'masters',
        'founders',
        'promoters'
      ].includes(kind)
    ) {
      const chapterRole = chapterRoleKey[kind];

      const orderedRoles = [...record.roles].sort(
        (a, b) => {
          const aPrimary =
            roleKey(a) === chapterRole ? 0 : 1;
          const bPrimary =
            roleKey(b) === chapterRole ? 0 : 1;

          return aPrimary - bPrimary;
        }
      );

      const roleValues = orderedRoles.length
        ? orderedRoles.slice(0, 2)
        : [labels[kind]];

      const roleBadges = roleValues
        .map((role, index) => badge(
          roleLabel(role) || role,
          index === 0 ? 'upcoming' : 'default'
        ))
        .join('');

      topline = `<div class="f2w-event-card__topline">${roleBadges}</div>`;

      details += line(
        'f2w-event-card__location',
        [record.country?.label || record.nationality],
        countryFlag()
      );

      const supporting = kind === 'promoters'
        ? record.organizations[0]?.label ||
          record.specialization ||
          record.locations[0]?.label
        : record.specialization ||
          record.locations[0]?.label;

      details += line(
        'f2w-event-card__organization',
        [supporting]
      );
    } else if (locationKinds.includes(kind)) {
      topline = `<div class="f2w-event-card__topline">${badge(record.typeLabel || labels[kind])}</div>`;

      details += line(
        'f2w-event-card__location',
        [record.city, record.country?.label],
        countryFlag()
      );
    } else if (kind === 'events' || kind === 'historic') {
      const parts = String(record.dateStart || '').split('-');

      if (parts.length === 3) {
        dateBlock = `<div class="f2w-event-card__date-block"><span class="f2w-event-card__date-day">${escapeHtml(parts[2])}</span><span class="f2w-event-card__date-month">${escapeHtml(parts[1])}</span><span class="f2w-event-card__date-year">${escapeHtml(parts[0])}</span></div>`;
      }

      topline = `<div class="f2w-event-card__topline">${badge(record.era || labels[kind], kind === 'historic' ? 'historic' : 'upcoming')}</div>`;

      details += line(
        'f2w-event-card__organization',
        [record.organizations[0]?.label]
      );

      details += line(
        'f2w-event-card__location',
        [
          record.locationName || record.location?.label,
          record.city,
          record.country?.label
        ],
        countryFlag()
      );

      if (kind === 'historic' && record.significance) {
        const significance = record.significance.length > 260
          ? `${record.significance.slice(0, 257)}...`
          : record.significance;

        details += line(
          'f2w-event-card__description',
          [significance]
        );
      }
    } else if (kind === 'organizations') {
      topline = `<div class="f2w-event-card__topline">${badge(record.typeLabel || labels.organizations)}</div>`;

      details += line(
        'f2w-event-card__location',
        [record.country?.label],
        countryFlag()
      );
    }

    const linkStart = record.url
      ? `<a class="f2w-event-card__link" href="${escapeHtml(record.url)}">`
      : '<div class="f2w-event-card__link">';

    const linkEnd = record.url
      ? '</a>'
      : '</div>';

    return `<article class="f2w-event-card" data-f2w-sport-card="true">${linkStart}<div class="f2w-event-card__image">${image}</div><div class="f2w-event-card__body">${dateBlock}<div class="f2w-event-card__content">${topline}<h2 class="f2w-event-card__title">${escapeHtml(record.name)}</h2>${details}${sports}${record.url ? `<span class="f2w-event-card__view">${escapeHtml(root.dataset.labelView || '')}</span>` : ''}</div></div>${linkEnd}</article>`;
  };

  const setupSection = (root) => {
    if (root.dataset.f2wInitialized === 'true') return;

    root.dataset.f2wInitialized = 'true';

    const sportId = root.dataset.sportId;
    const sportHandle = normalize(root.dataset.sportHandle);
    const imageMap = new Map();
    const loadedImagePages = new Map();
    const totalImagePages = new Map();
    const imagePageRequests = new Map();

    mergeImageMaps(
      root,
      imageMap,
      loadedImagePages,
      totalImagePages
    );

    const panels = [
      ...root.querySelectorAll('[data-f2w-sport-panel]')
    ];
    const links = [
      ...root.querySelectorAll('[data-f2w-sport-chapter]')
    ];
    const cache = new Map();
    const loadingPromises = new Map();
    const activePages = new Map();

    const setActive = (chapter, updateUrl = false) => {
      const selected = panels.some(
        (panel) => panel.dataset.f2wSportPanel === chapter
      )
        ? chapter
        : 'overview';

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.f2wSportPanel !== selected;
      });

      links.forEach((link) => {
        const selectedLink =
          link.dataset.f2wSportChapter === selected;

        link.classList.toggle('is-active', selectedLink);
        link.setAttribute(
          'aria-selected',
          selectedLink ? 'true' : 'false'
        );
        link.setAttribute(
          'aria-current',
          selectedLink ? 'page' : 'false'
        );

        if (selectedLink) {
          link.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      });

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('chapter', selected);
        window.history.replaceState({}, '', url.toString());
      }

      const panel = panels.find(
        (item) => item.dataset.f2wSportPanel === selected
      );
      const directory = panel?.dataset.f2wSportDirectory;

      if (panel && directory) {
        loadDirectory(panel, directory);
      }
    };

    const optionValues = (records, getter) => [
      ...new Set(records.map(getter).filter(Boolean))
    ].sort((a, b) => a.localeCompare(
      b,
      document.documentElement.lang || 'nl-NL'
    ));

    const addOptions = (select, values) => {
      if (
        !select ||
        select.dataset.f2wOptionsReady === 'true'
      ) {
        return;
      }

      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });

      select.dataset.f2wOptionsReady = 'true';
    };

    const filterValue = (panel, name) => normalize(
      panel.querySelector(
        `[data-f2w-filter-${name}]`
      )?.value
    );

    const includesValue = (values, selected) => {
      if (!selected) return true;

      return values.some(
        (value) => normalize(value) === selected
      );
    };

    const filteredRecords = (panel, records) => {
      const selected = {
        search: filterValue(panel, 'search'),
        country: filterValue(panel, 'country'),
        city: filterValue(panel, 'city'),
        region: filterValue(panel, 'region'),
        role: filterValue(panel, 'role'),
        gym: filterValue(panel, 'gym'),
        tradition: filterValue(panel, 'tradition'),
        organization: filterValue(panel, 'organization'),
        function: filterValue(panel, 'function'),
        type: filterValue(panel, 'type'),
        era: filterValue(panel, 'era'),
        decade: filterValue(panel, 'decade')
      };

      return records.filter((record) => {
        const searchable = [
          record.name,
          record.description,
          record.city,
          record.region,
          record.typeLabel,
          record.era,
          record.decade,
          ...record.countries.map((item) => item.label),
          ...record.locations.map((item) => item.label),
          ...record.organizations.map((item) => item.label),
          ...record.roles,
          ...record.traditions,
          ...record.functions
        ].join(' ');

        if (
          selected.search &&
          !normalize(searchable).includes(selected.search)
        ) {
          return false;
        }

        if (!includesValue(
          record.countries.map((item) => item.label),
          selected.country
        )) {
          return false;
        }

        if (
          selected.city &&
          normalize(record.city) !== selected.city
        ) {
          return false;
        }

        if (
          selected.region &&
          normalize(record.region) !== selected.region
        ) {
          return false;
        }

        if (!includesValue(record.roles, selected.role)) {
          return false;
        }

        if (!includesValue(
          record.locations.map((item) => item.label),
          selected.gym
        )) {
          return false;
        }

        if (!includesValue(
          record.traditions,
          selected.tradition
        )) {
          return false;
        }

        if (!includesValue(
          record.organizations.map((item) => item.label),
          selected.organization
        )) {
          return false;
        }

        if (!includesValue(
          record.functions,
          selected.function
        )) {
          return false;
        }

        if (
          selected.type &&
          normalize(record.typeLabel) !== selected.type
        ) {
          return false;
        }

        if (
          selected.era &&
          normalize(record.era) !== selected.era
        ) {
          return false;
        }

        if (
          selected.decade &&
          normalize(record.decade) !== selected.decade
        ) {
          return false;
        }

        return true;
      });
    };

    const render = (panel, kind, records) => {
      const loading = panel.querySelector(
        '[data-f2w-sport-loading]'
      );
      const empty = panel.querySelector(
        '[data-f2w-sport-empty]'
      );
      const grid = panel.querySelector(
        '[data-f2w-sport-results]'
      );
      const status = panel.querySelector(
        '[data-f2w-sport-status]'
      );
      const matching = filteredRecords(panel, records);
      const perPage = [
        'organizations',
        'promoters'
      ].includes(kind)
        ? 24
        : 48;
      const totalPages = Math.max(
        1,
        Math.ceil(matching.length / perPage)
      );
      const currentPage = Math.min(
        activePages.get(kind) || 1,
        totalPages
      );
      const pageItems = matching.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage
      );
      let pagination = panel.querySelector(
        '[data-f2w-sport-pagination]'
      );

      if (!pagination) {
        pagination = document.createElement('nav');
        pagination.className =
          'f2w-event-directory__pagination fd-relation-list';
        pagination.dataset.f2wSportPagination = '';
        pagination.setAttribute(
          'aria-label',
          panel.querySelector('h2')?.textContent?.trim() || ''
        );
        grid.insertAdjacentElement(
          'afterend',
          pagination
        );
      }

      loading.hidden = true;
      grid.innerHTML = pageItems
        .map((record) => makeCard(root, panel, record, kind))
        .join('');
      grid.hidden = pageItems.length === 0;
      empty.hidden = matching.length > 0;
      status.textContent = matching.length
        ? String(matching.length)
        : '';
      pagination.replaceChildren();
      pagination.hidden =
        totalPages <= 1 ||
        matching.length === 0;

      if (!pagination.hidden) {
        for (
          let page = 1;
          page <= totalPages;
          page += 1
        ) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'fd-relation-pill';
          button.textContent = String(page);
          button.setAttribute(
            'aria-label',
            `${panel.querySelector('h2')?.textContent?.trim() || ''} ${page}`.trim()
          );

          if (page === currentPage) {
            button.classList.add('cib__bekijk-btn');
            button.setAttribute(
              'aria-current',
              'page'
            );
          }

          button.addEventListener('click', () => {
            activePages.set(kind, page);
            render(panel, kind, records);
            panel.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          });

          pagination.appendChild(button);
        }
      }
    };

    const setupFilters = (panel, kind, records) => {
      if (panel.dataset.f2wFiltersReady === 'true') {
        return;
      }

      const values = {
        country: records.flatMap(
          (record) => record.countries.map((item) => item.label)
        ),
        city: records.map((record) => record.city),
        region: records.map((record) => record.region),
        role: records.flatMap((record) => record.roles),
        gym: records.flatMap(
          (record) => record.locations.map((item) => item.label)
        ),
        tradition: records.flatMap(
          (record) => record.traditions
        ),
        organization: records.flatMap(
          (record) => record.organizations.map((item) => item.label)
        ),
        function: records.flatMap(
          (record) => record.functions
        ),
        type: records.map((record) => record.typeLabel),
        era: records.map((record) => record.era),
        decade: records.map((record) => record.decade)
      };

      Object.entries(values).forEach(([name, list]) => {
        addOptions(
          panel.querySelector(
            `[data-f2w-filter-${name}]`
          ),
          optionValues(list, (value) => value)
        );
      });

      const controls = [
        ...panel.querySelectorAll(
          '[data-f2w-sport-filters] input, [data-f2w-sport-filters] select'
        )
      ];

      const apply = () => {
        activePages.set(kind, 1);
        render(panel, kind, records);
      };

      controls.forEach((control) => {
        control.addEventListener(
          control.type === 'search' ? 'input' : 'change',
          apply
        );
      });

      panel.querySelector(
        '[data-f2w-filter-reset]'
      )?.addEventListener('click', () => {
        controls.forEach((control) => {
          control.value = '';
        });

        apply();
      });

      panel.dataset.f2wFiltersReady = 'true';
    };

    const requestImagePage = async (type, page) => {
      const requestKey = `${type}:${page}`;

      if (loadedImagePages.get(type)?.has(page)) {
        return;
      }

      if (imagePageRequests.has(requestKey)) {
        return imagePageRequests.get(requestKey);
      }

      const pageRequest = (async () => {
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(page));

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error(
            'Image fallback page request failed'
          );
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(
          html,
          'text/html'
        );
        const parsedRoot = parsed.querySelector(
          '[data-f2w-sport-detail-final]'
        );

        if (!parsedRoot) {
          throw new Error(
            'Sport image fallback was not found'
          );
        }

        mergeImageMaps(
          parsedRoot,
          imageMap,
          loadedImagePages,
          totalImagePages
        );
      })();

      imagePageRequests.set(requestKey, pageRequest);

      try {
        await pageRequest;
      } finally {
        imagePageRequests.delete(requestKey);
      }
    };

    const ensureImages = async (records, types) => {
      for (const type of types) {
        const recordsForType = records.filter(
          (record) => record.metaobjectType === type
        );
        let nextPage = 1;

        const pages = () => Math.max(
          1,
          totalImagePages.get(type) || 1
        );

        const hasMissingFallback = () => recordsForType.some(
          (record) => {
            return !record.image &&
              record.handle &&
              !imageMap.has(`${type}:${record.handle}`);
          }
        );

        while (
          hasMissingFallback() &&
          nextPage <= pages()
        ) {
          if (!loadedImagePages.get(type)?.has(nextPage)) {
            try {
              await requestImagePage(type, nextPage);
            } catch (error) {
              break;
            }
          }

          nextPage += 1;
        }

        recordsForType.forEach((record) => {
          if (!record.image && record.handle) {
            record.image = imageMap.get(
              `${type}:${record.handle}`
            ) || '';
          }
        });
      }
    };

    const loadDirectory = async (panel, kind) => {
      if (cache.has(kind)) {
        const records = cache.get(kind);
        setupFilters(panel, kind, records);
        render(panel, kind, records);
        return;
      }

      if (loadingPromises.has(kind)) {
        await loadingPromises.get(kind);

        if (cache.has(kind)) {
          const records = cache.get(kind);
          setupFilters(panel, kind, records);
          render(panel, kind, records);
        }

        return;
      }

      const loading = panel.querySelector(
        '[data-f2w-sport-loading]'
      );
      const error = panel.querySelector(
        '[data-f2w-sport-error]'
      );
      const empty = panel.querySelector(
        '[data-f2w-sport-empty]'
      );
      const grid = panel.querySelector(
        '[data-f2w-sport-results]'
      );

      const promise = (async () => {
        try {
          panel.dataset.f2wSportState = 'loading';
          loading.hidden = false;
          error.hidden = true;
          empty.hidden = true;
          grid.hidden = true;

          const types = TYPES[kind] || [];
          const records = [];
          const localizedLanguage = storefrontLanguage(root);
          const canonicalLanguage = sourceLanguage(root);

          for (const type of types) {
            const localizedRaw = await fetchAll(
              root,
              type,
              localizedLanguage
            );
            let classificationRaw = localizedRaw;

            if (
              canonicalLanguage &&
              canonicalLanguage !== localizedLanguage
            ) {
              classificationRaw = await fetchAll(
                root,
                type,
                canonicalLanguage
              );
            }

            const classificationById = new Map(
              classificationRaw.map((record) => [
                record.id,
                record
              ])
            );

            records.push(
              ...localizedRaw.map((record) => {
                return readRecord(
                  record,
                  imageMap,
                  type,
                  classificationById.get(record.id) || record
                );
              })
            );
          }

          await ensureImages(records, types);

          const unique = new Map();

          records
            .filter((record) => {
              if (!record.name || !matchesKind(record, kind)) {
                return false;
              }

              return record.sports.some((sport) => {
                const handleMatches =
                  sportHandle &&
                  normalize(sport.handle) === sportHandle;
                const idMatches =
                  sportId &&
                  sport.id === sportId;

                return handleMatches || idMatches;
              });
            })
            .forEach((record) => {
              unique.set(record.id, record);
            });

          const result = [...unique.values()].sort((a, b) => {
            return a.name.localeCompare(
              b.name,
              document.documentElement.lang || 'nl-NL'
            );
          });

          cache.set(kind, result);
          setupFilters(panel, kind, result);
          render(panel, kind, result);
          panel.dataset.f2wSportState = 'ready';
        } catch (loadError) {
          panel.dataset.f2wSportState = 'error';
          loading.hidden = true;
          error.hidden = false;
          empty.hidden = true;
          grid.hidden = true;
          grid.replaceChildren();
        } finally {
          loadingPromises.delete(kind);
        }
      })();

      loadingPromises.set(kind, promise);
      await promise;
    };

    links.forEach((link) => link.addEventListener('click', (event) => {
      event.preventDefault();
      setActive(link.dataset.f2wSportChapter, true);
    }));

    window.addEventListener('popstate', () => {
      setActive(
        new URLSearchParams(window.location.search).get('chapter') ||
        'overview'
      );
    });

    setActive(
      new URLSearchParams(window.location.search).get('chapter') ||
      'overview'
    );
  };

  sections.forEach(setupSection);
})();
